import React, { useState, useEffect } from "react";
import { FaUserMd } from "react-icons/fa";
import { Stethoscope } from "lucide-react";
import { API_BASE } from "../config";

export function MedecinsAdmin({ user }) {

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedMedecin, setSelectedMedecin] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toast, setToast] = useState({ type: "", msg: "" });

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };
  const limit = 10;

  // =========================
  // FETCH LIST
  // =========================

  const fetchMedecins = async () => {
    setLoading(true);
    setError("");

    try {

      const params = new URLSearchParams();

      params.set("page", page);
      params.set("limit", limit);

      if (q) params.set("q", q);
      if (status) params.set("actif", status);

      const res = await fetch(
        `${API_BASE}/admin/medecins?${params}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.msg || "Erreur chargement médecins");
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }

    } catch {
      setError("Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedecins();
  }, [page, status]);

  const pages = Math.max(1, Math.ceil(total / limit));

  // =========================
  // FETCH DETAIL
  // =========================

  const fetchMedecinDetail = async (id) => {

    setDetailLoading(true);

    try {

      const res = await fetch(
        `${API_BASE}/admin/medecins/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.msg || "Erreur chargement médecin");
        return;
      }

      setSelectedMedecin(data);

    } catch {
      setError("Impossible de charger les détails.");
    } finally {
      setDetailLoading(false);
    }
  };

  // =========================
  // TOGGLE ACTIF
  // =========================

  const doToggle = async (id, nextActif) => {
    setActionLoading(true);
    setError("");
  
    try {
      const res = await fetch(
        `${API_BASE}/admin/medecins/${id}/actif`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ actif: nextActif }),
        }
      );
  
      const data = await res.json();
  
      if (!res.ok) {
        throw new Error(data.msg || "Erreur mise à jour");
      }
  
      setItems((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, actif: nextActif } : m
        )
      );
  
      setSuccess(
        nextActif
          ? "Médecin activé avec succès"
          : "Médecin désactivé avec succès"
      );
  
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      setError(e.message || "Erreur mise à jour.");
    } finally {
      setActionLoading(false);
    }
  };

  // =========================
  // UI
  // =========================

  return (

    <div className="adminPanel">

      {/* HEADER */}

      <div className="adminPanelHeader">

        <div>
          <div className="adminPanelTitle">
          </div>

          <div className="adminPanelSub">
          </div>
        </div>

        <button
            className="btnSecondary"
            onClick={async () => {
              setPage(1);
              await fetchMedecins();
              showToast("success", "Liste des médecins mise à jour");
            }}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>

      </div>

      {/* TOOLBAR */}

      <div className="adminToolbar">

        <input
          className="adminInput"
          placeholder="Rechercher par nom ou email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              fetchMedecins();
            }
          }}
        />

        <select
          className="adminSelect"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >

          <option value="">Tous les statuts</option>
          <option value="true">Actif</option>
          <option value="false">Inactif</option>

        </select>

        <button
          className="btnPrimary"
          onClick={() => {
            setPage(1);
            fetchMedecins();
          }}
        >
          Rechercher
        </button>

      </div>

      {error && <div className="adminError">{error}</div>}
      {success && <div className="adminSuccess">{success}</div>}
      {toast.msg && (
        <div className={`adminToast ${toast.type}`}>
          {toast.msg}
        </div>
      )}

      {/* TABLE */}

      <div className="adminTableWrap">

        <table className="adminTable">

          <thead>
            <tr>
              <th>Médecin</th>
              <th>Spécialité</th>
              <th>Email</th>
              <th>Ville</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="adminEmpty">
                  Aucun médecin trouvé.
                </td>
              </tr>
            )}

            {items.map((m) => (

              <tr key={m.id}>

                <td style={{ fontSize: 12 }}>
                  <div className="doctorCell">

                    <div className="doctorAvatar">
                    <Stethoscope size={20} color="#2563eb" />
                                        </div>

                    <div className="doctorInfo">
                      <div className="doctorName">
                        Dr. {m.nom}
                      </div>
                    </div>

                  </div>
                </td>

                <td style={{ fontSize: 12 }}>
                  <span className="specialiteBadge">
                    {m.specialite}
                  </span>
                </td>

                <td style={{ fontSize: 12 }}>
                  {m.email}
                </td>

                <td style={{ fontSize: 12 }}>
                  {m.ville}
                </td>

                <td style={{ fontSize: 12 }}>
                  <span className={`statusBadge ${m.actif ? "on" : "off"}`}>
                    {m.actif ? "Actif" : "Inactif"}
                  </span>
                </td>

                <td style={{ display: "flex", gap: 8 }}>

                <button
                  className={`toggleBtn ${m.actif ? "on" : "off"}`}
                  onClick={() =>
                    setConfirm({
                      id: m.id,
                      nom: m.nom,
                      nextActif: !m.actif,
                    })
                  }
                >
                  {m.actif ? "Désactiver" : "Activer"}
                </button>
                  <button
                    className="btnSecondaryOutline"
                    onClick={() => fetchMedecinDetail(m.id)}
                  >
                    Voir
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* PAGINATION */}

      <div className="adminPager">

        <button
          className="btnSecondary"
          onClick={() =>
            setPage((p) => Math.max(1, p - 1))
          }
          disabled={page === 1}
        >
          ←
        </button>

        <div className="adminPagerInfo">
          Page <b>{page}</b> / {pages} — Total: <b>{total}</b>
        </div>

        <button
          className="btnSecondary"
          onClick={() =>
            setPage((p) => Math.min(pages, p + 1))
          }
          disabled={page >= pages}
        >
          →
        </button>

      </div>

      {/* MODAL DETAIL */}

      {selectedMedecin && (

        <div className="logoutModalOverlay">

          <div className="medecinModal">

            <div className="medecinHeader">

              <div className="medecinAvatar">
              <Stethoscope size={20} color="#2563eb" />              </div>

              <div>

                <div className="medecinTitle">
                  Dr. {selectedMedecin.nom}
                </div>

                <div className="medecinBadges">

                  <span className="badgeSpecialite">
                    {selectedMedecin.specialite}
                  </span>

                  <span className={`badgeStatus ${selectedMedecin.actif ? "on" : "off"}`}>
                    {selectedMedecin.actif ? "Actif" : "Inactif"}
                  </span>

                </div>

              </div>

            </div>

            <div className="medecinBody">

              {detailLoading ? (
                <div>Chargement...</div>
              ) : (

                <div className="medecinGrid">

                  <div className="medecinItem">
                    <div className="label">Email</div>
                    <div className="value">{selectedMedecin.email}</div>
                  </div>

                  <div className="medecinItem">
                    <div className="label">Téléphone</div>
                    <div className="value">{selectedMedecin.telephone || "—"}</div>
                  </div>

                  <div className="medecinItem">
                    <div className="label">Ville</div>
                    <div className="value">{selectedMedecin.ville}</div>
                  </div>

                  <div className="medecinItem">
                    <div className="label">Agrément</div>
                    <div className="value">{selectedMedecin.agrement || "—"}</div>
                  </div>

                </div>

              )}

            </div>

            <div className="medecinFooter">

              <button
                className="btnSecondary"
                onClick={() => setSelectedMedecin(null)}
              >
                Fermer
              </button>

            </div>

          </div>

        </div>

      )}
      {confirm && (
  <div className="logoutModalOverlay">
    <div className="logoutModal">

      <div className="logoutModalTitle">
        Confirmation
      </div>

      <div className="logoutModalText">
        {confirm.nextActif
          ? `Activer le médecin : Dr. ${confirm.nom} ?`
          : `Désactiver le médecin : Dr. ${confirm.nom} ?`}
      </div>

      <div className="logoutModalActions">

        <button
          className="btnSecondary"
          onClick={() => setConfirm(null)}
        >
          Annuler
        </button>

        <button
          className="btnDanger"
          onClick={() => {
            doToggle(confirm.id, confirm.nextActif);
            setConfirm(null);
          }}
          disabled={actionLoading}
        >
          {actionLoading ? "Chargement..." : "Confirmer"}
        </button>

      </div>

    </div>
  </div>
)}

    </div>

  );
}